/**
 * OpenRouteService provider — vyžaduje bezplatný API klíč
 * (https://openrouteservice.org). Klíč se čte z env proměnné
 * VITE_ORS_API_KEY, nebo z lokálního nastavení; nikdy není v kódu.
 *
 * Trasa: síť OpenStreetMap přes ORS Directions API (GeoJSON).
 * Délka v metrech, doba v sekundách dle profilu.
 */
import type { LatLng, RoutingProfile } from '../../core/types';
import type { ProviderConfig, ProviderRoute, RoutingProvider } from './types';

const ORS_PROFILE: Record<RoutingProfile, string> = {
  car: 'driving-car',
  bike: 'cycling-regular',
  foot: 'foot-walking',
};

export const orsProvider: RoutingProvider = {
  id: 'ors',
  name: 'OpenRouteService',
  description:
    'Directions API nad daty OpenStreetMap. Vyžaduje bezplatný klíč (VITE_ORS_API_KEY nebo expertní nastavení).',
  requiresKey: true,
  isConfigured: (cfg) => Boolean(cfg.orsApiKey && cfg.orsApiKey.length > 10),

  async route(
    profile: RoutingProfile,
    from: LatLng,
    to: LatLng,
    cfg: ProviderConfig,
  ): Promise<ProviderRoute> {
    if (!cfg.orsApiKey) throw new Error('MissingKey');
    const url = `https://api.openrouteservice.org/v2/directions/${ORS_PROFILE[profile]}/geojson`;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), cfg.timeoutMs ?? 8000);
    try {
      const res = await fetch(url, {
        method: 'POST',
        signal: ctrl.signal,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/geo+json',
          Authorization: cfg.orsApiKey,
        },
        body: JSON.stringify({
          coordinates: [
            [from.lng, from.lat],
            [to.lng, to.lat],
          ],
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as {
        features?: Array<{
          properties?: { summary?: { distance?: number; duration?: number } };
          geometry?: { coordinates?: Array<[number, number]> };
        }>;
      };
      const f = data.features?.[0];
      const coords = f?.geometry?.coordinates;
      const summary = f?.properties?.summary;
      if (!coords || coords.length < 2 || !summary || !Number.isFinite(summary.distance)) {
        throw new Error('NoRoute');
      }
      return {
        geometry: coords.map(([lng, lat]) => ({ lat, lng })),
        distanceKm: (summary.distance as number) / 1000,
        durationMin: Number.isFinite(summary.duration) ? (summary.duration as number) / 60 : null,
      };
    } finally {
      clearTimeout(timer);
    }
  },
};
